import React, { useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { closeModal, openModal } from "react-url-modal";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { ITournament, IApplyTournamentData } from "@/redux/tournaments/types";
import {
  fetchApplyTournament,
  fetchGetMyApplicationsTournaments,
  fetchGetAvailableTournaments,
} from "@/redux/tournaments";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { Button, NameInput, PhoneInput } from "@/components";
import { TournamentLevelInfo } from "@/pages/Tournaments/modals/TournamentLevelInfo";
import styles from "./JoinTournamentContent.module.scss";

interface JoinTournamentContentProps {
  tournament: ITournament;
  onLevelInfoClick?: (level: string) => void;
}

const LEVEL_OPTIONS = [
  { label: "1.0", value: 1.0 },
  { label: "1.5", value: 1.5 },
  { label: "2.0", value: 2.0 },
  { label: "2.5", value: 2.5 },
  { label: "3.0", value: 3.0 },
  { label: "3.5", value: 3.5 },
  { label: "4.0", value: 4.0 },
  { label: "4.5", value: 4.5 },
  { label: "5.0", value: 5.0 },
  { label: "5.5", value: 5.5 },
  { label: "6.0", value: 6.0 },
  { label: "6.5", value: 6.5 },
  { label: "7.0", value: 7.0 },
];

export const JoinTournamentContent: React.FC<JoinTournamentContentProps> = ({
  tournament,
  onLevelInfoClick,
}) => {
  const dispatch = useAppDispatch();
  const profileInfo = useAppSelector(getProfileInfo);
  const [levelInfoModalVisible, setLevelInfoModalVisible] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string>("");

  const initialValues = {
    name: profileInfo.profileInfo?.name || "",
    contactPhone: profileInfo.profileInfo?.phone || "",
    level: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Введите имя"),
    contactPhone: Yup.string()
      .required("Введите телефон")
      .test("phone-length", "Некорректный номер телефона", (value) => {
        return value ? value.replace(/[^+0-9]/g, "").length === 12 : false;
      }),
    level: Yup.string().required("Выберите уровень"),
  });

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    try {
      const data: IApplyTournamentData = {
        level: parseFloat(values.level),
        name: values.name.trim(),
        contactPhone: `+${values.contactPhone.replace(/[^0-9]/g, "")}`,
      };

      await dispatch(fetchApplyTournament({ id: tournament.id, data })).unwrap();
      
      // Обновляем списки
      await Promise.all([
        dispatch(fetchGetMyApplicationsTournaments()),
        dispatch(fetchGetAvailableTournaments()),
      ]);

      closeModal();
      openModal({ name: "successRequestSend" });
    } catch (error) {
      console.error("Error applying to tournament:", error);
      // TODO: Показать ошибку пользователю
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const isDisabled = formik.isSubmitting || !formik.isValid;

          return (
            <Form className={styles.form}>
            <div className={styles.formSection}>
              <PhoneInput
                name="contactPhone"
                lable="Номер для связи с Вами"
                placeholder="+7 (___) ___-__-__"
              />
              <ErrorMessage component="div" className={styles.errorMessage} name="contactPhone" />
            </div>

            <div className={styles.formSection}>
              <NameInput
                name="name"
                lable="Имя"
                placeholder="Введите имя"
              />
              <ErrorMessage component="div" className={styles.errorMessage} name="name" />
            </div>

            <div className={styles.formSection}>
              <label className={styles.label}>Уровень</label>
              <div className={styles.levelContainer}>
                {LEVEL_OPTIONS.map((level) => (
                  <div key={level.value} className={styles.levelOption}>
                    <label className={styles.levelLabel}>
                      <input
                        type="radio"
                        name="level"
                        value={String(level.value)}
                        checked={formik.values.level === String(level.value)}
                        onChange={(e) => formik.setFieldValue("level", e.target.value)}
                      />
                      <span>{level.label}</span>
                    </label>
                  </div>
                ))}
              </div>
              <ErrorMessage component="div" className={styles.errorMessage} name="level" />
            </div>

            <Button
              type="submit"
              disabled={isDisabled}
              newStyles={styles.submitButton}
            >
              {formik.isSubmitting ? "Отправка..." : "Отправить заявку"}
            </Button>
          </Form>
        );
      }}
      </Formik>
      {levelInfoModalVisible && (
        <TournamentLevelInfo
          isOpen={levelInfoModalVisible}
          setIsOpen={setLevelInfoModalVisible}
          level={selectedLevel}
        />
      )}
    </>
  );
};
